/**
 * Класс placeHTML.
 *
 * Основные возможности:
 *  - Размещение переданного HTML (или загруженного по URL) в заданном месте (селектор/DOM-элемент/stack).
 *  - Поддержка типов: `html`, `modal`, `toast`, `alert` (можно расширять).
 *  - Опции загрузки: GET/POST, динамические параметры, обработка ошибок.
 *  - Отложенная вставка (delayed).
 *  - Автоматическое закрытие (autoClose).
 *  - Кнопка закрытия (hasClose).
 *  - При type=toast/alert, если placeIn=stack, создаются stack-контейнеры.
 *  - События (колбэки): onInit, beforeLoad, beforePlace, afterPlace, onHide, onDelete.
 *  - Дебаг-режим (debug=true) — вывод логов в консоль.
 *  - Удаление DOM-элемента после закрытия (deleteAfterClose=true).
 *  - Для modal — загрузка по URL + инициализация Bootstrap.Modal.
 *  - Для toast/alert — если нет url, генерируем верстку самостоятельно.
 *  - Поддержка массива кнопок (buttons) для toast/alert.
 *  - Авто-создание stack-контейнеров.
 *  - Связываем объект класса с DOM-элементом (instance).
 *  - Явное указание caller для добавления класса.
 *  - Спиннер может появляться как через CSS (не меняя DOM), так и через spinner:true (меняя DOM)
 *
 * v.0.2.250414 - текущая версия
 * v.0.2.250505 - добавлен backdrop
 * v.0.3.250808 - поправил вызов коллбеков типа afterPlace
 * v.0.4.250927 - поправил мелкий баг с закрытием модалок
 * v.0.5.250930 - пофиксил баг с backdrop + всякая мелочь
 * v.0.6.251026 - добавлен icon
 * v.0.7.251203 - bodyClass__loading (например, чтобы блокировать всё до загрузки)
 */

class placeHTML {
  /**
   * Статические опции по умолчанию.
   */
  static defaultOptions = {
    placeIn: 'body',
    url: false,
    method: 'GET',
    fetchParams: {
			headers: { 'X-Requested-With': 'XMLHttpRequest' }
		},
    type: 'html',
    html: false,
    position: 'beforeEnd',
    debug: false,
    delayed: false,
    autoClose: false,
    deleteAfterClose: true,
    hasClose: false,
    backdrop: false,
    backdropClassMain: '_backdrop',
    backdropClass: '',
    backdropStatic: false,
    class: '',
    params: {},
    paramsType: 'default',
    stackId: '',
    stackPosition: 'top center',
    title: '',
    body: '',
    buttons: [],
    icon: '', // HTML значка или <img ...>
    onInit: null,
    beforeLoad: null,
    beforePlace: null,
    afterPlace: null,
    onHide: null,
    onDelete: null,
    placeHTMLclass: '_dynamic',
    dataAttrName: 'data-placehtml',
    caller: null, // this - указывать для работы loadingClass или spinner
    spinner: false,
    loadingClass: '_loading',
    bodyClass__loading: ''
  }

  /**
   * Глобальный пул бэкдропов: Map<key, { el, count, escHandler }>
   * key = stackId (если есть) иначе `${type}-global`
   */
  static _backdrops = new Map()

  constructor(userOptions = {}) {
    this._closing = false
    this._mergeOptions(userOptions)
    this._prepareCallbacksFromStrings()
    this._setupCallbacks()
    this._setupCaller()

    this._debug(
      'init',
      'Инициализация placeHTML, итоговые опции:',
      this.options
    )
    if (typeof this.onClassInit === 'function') {
      this.onClassInit(this)
    }

    if (this.options.delayed && Number(this.options.delayed) > 0) {
      setTimeout(() => this.process(), this.options.delayed)
    } else {
      this.process()
    }
  }

  _mergeOptions(userOptions) {
    const typeDefaultOptions = {
      modal: {
        placeIn: 'body',
        backdrop: true
      },

      toast: {
        placeIn: 'stack',
        stackPosition: 'top center',
        backdropClass: '_backdrop-toast'
      },

      alert: {
        placeIn: 'stack',
        stackPosition: 'top center',
        backdropClass: '_backdrop-alert'
      },

      html_place: {
        position: 'beforeEnd'
      }
    }

    let merged = { ...placeHTML.defaultOptions }
    if (userOptions.type && typeDefaultOptions[userOptions.type]) {
      merged = { ...merged, ...typeDefaultOptions[userOptions.type] }
    }

    this.options = { ...merged, ...userOptions }
    if (this.options.debug) console.log(this.options)
  }

  _setupCallbacks() {
    this.onClassInit = this.options.onInit
    this.beforeClassLoad = this.options.beforeLoad
    this.beforeClassPlace = this.options.beforePlace
    this.afterClassPlace = this.options.afterPlace
    this.onClassHide = this.options.onHide
    this.onClassDelete = this.options.onDelete
  }

  _setupCaller() {
    if (this.options.caller) {
      this.originalCallerHTML =
        this.options.spinner && this.options.placeIn !== this.options.caller
          ? this.options.caller.innerHTML
          : null
    } else {
      this.originalCallerHTML = null
    }
  }

  _prepareCallbacksFromStrings() {
    const cbNames = [
      'onInit',
      'beforeLoad',
      'beforePlace',
      'afterPlace',
      'onHide',
      'onDelete'
    ]
    cbNames.forEach((name) => {
      const cb = this.options[name]
      if (typeof cb === 'string') {
        try {
          this.options[name] = new Function('instance', cb)
        } catch (e) {
          this._debug('error', `Ошибка парсинга коллбэка ${name}:`, e)
          this.options[name] = null
        }
      }
    })
  }

  process() {
    // Добавляем класс на body (если указан)
    if (this.options.bodyClass__loading && this.options.url) {
      document.body.classList.add(this.options.bodyClass__loading)
    }

    if (this.options.caller) {
      this.options.caller.classList.add(this.options.loadingClass)
      if (this.options.spinner) {
        const spinnerHTML =
          this.options.spinner === true
            ? '<div class="spinner-border" role="status"></div>'
            : typeof this.options.spinner === 'string'
            ? this.options.spinner
            : ''
        if (spinnerHTML) {
          this.options.caller.innerHTML = spinnerHTML
        }
      }
    }

    if (this.options.url) {
      this._fetchContent()
        .then((content) => {
          this._clearSpinner()
          this._placeContent(content)
          this._finalizeCaller()
        })
        .catch((err) => {
          this._debug('error', 'Ошибка загрузки:', err)
          this._clearSpinner()
          this._finalizeCaller()
        })
    } else {
      let finalContent = this.options.html
      if (!finalContent) {
        if (this.options.type === 'toast') {
          finalContent = this._createToastHTML()
        } else if (this.options.type === 'alert') {
          finalContent = this._createAlertHTML()
        }
      }
      this._clearSpinner()
      this._placeContent(finalContent)
      this._finalizeCaller()
    }
  }

  _clearSpinner() {
    if (this.options.caller && this.options.spinner) {
      if (this.options.placeIn === this.options.caller) {
        this.options.caller.innerHTML = ''
      }
    }
  }

  _finalizeCaller() {
    // Убираем класс с body
    if (this.options.bodyClass__loading) {
      document.body.classList.remove(this.options.bodyClass__loading)
    }

    if (this.options.caller) {
      this.options.caller.classList.remove(this.options.loadingClass)
      if (
        this.options.spinner &&
        this.originalCallerHTML !== null &&
        this.options.placeIn !== this.options.caller
      ) {
        this.options.caller.innerHTML = this.originalCallerHTML
      }
    }
  }

  _fetchContent() {
    return new Promise((resolve, reject) => {
      if (typeof this.beforeClassLoad === 'function') {
        this.beforeClassLoad(this)
      }

      let url = this.options.url
      const fetchOptions = {
        method: this.options.method,
        ...this.options.fetchParams
      }
      const params = this.options.params || {}

      if (Object.keys(params).length > 0) {
        if (this.options.method.toUpperCase() === 'GET') {
          const queryString = new URLSearchParams(params).toString()
          url += (url.includes('?') ? '&' : '?') + queryString
        } else {
          if (this.options.paramsType === 'json') {
            fetchOptions.headers = {
              ...fetchOptions.headers,
              'Content-Type': 'application/json'
            }
            fetchOptions.body = JSON.stringify(params)
          } else {
            fetchOptions.headers = {
              ...fetchOptions.headers,
              'Content-Type': 'application/x-www-form-urlencoded'
            }
            fetchOptions.body = new URLSearchParams(params)
          }
        }
      }

      this._debug(
        'info',
        `URL запрос: ${url}, метод: ${this.options.method}`,
        fetchOptions
      )
      fetch(url, fetchOptions)
        .then((response) => {
          if (!response.ok) throw new Error(`HTTP error: ${response.status}`)
          return response.text()
        })
        .then((html) => resolve(html))
        .catch((err) => reject(err))
    })
  }

  _placeContent(content) {
    if (!content) {
      this._debug('warn', 'Контент для вставки отсутствует')
      return
    }

    if (typeof this.beforeClassPlace === 'function') {
      this.beforeClassPlace(this)
    }

    this.element = this._resolvePlaceIn(this.options.placeIn)
    if (!this.element) {
      this._debug(
        'error',
        `Элемент для вставки не найден: ${this.options.placeIn}`
      )
      return
    }

    const tmpEl = document.createElement('div')
    tmpEl.innerHTML = content.trim()

    const domToInsert =
      tmpEl.childNodes.length === 1
        ? tmpEl.firstChild
        : Array.from(tmpEl.childNodes)

    if (
      this.options.class &&
      domToInsert instanceof Element &&
      !Array.isArray(domToInsert)
    ) {
      domToInsert.classList.add(
        ...this.options.class.split(' ').filter(Boolean)
      )
    }

    switch (this.options.position) {
      case 'innerHTML':
        this.element.innerHTML = content
        this.createdEl = this.element.firstChild
        break
      case 'outerHTML':
        this.element.outerHTML = content
        this.createdEl = null
        break
      case 'beforeBegin':
        if (Array.isArray(domToInsert)) {
          domToInsert.forEach((node) =>
            this.element.insertAdjacentElement('beforebegin', node)
          )
          this.createdEl = domToInsert[domToInsert.length - 1]
        } else {
          this.element.insertAdjacentElement('beforebegin', domToInsert)
          this.createdEl = domToInsert
        }
        break
      case 'afterBegin':
        if (Array.isArray(domToInsert)) {
          domToInsert
            .reverse()
            .forEach((node) =>
              this.element.insertAdjacentElement('afterbegin', node)
            )
          this.createdEl = domToInsert[0]
        } else {
          this.element.insertAdjacentElement('afterbegin', domToInsert)
          this.createdEl = domToInsert
        }
        break
      case 'afterEnd':
        if (Array.isArray(domToInsert)) {
          domToInsert.forEach((node) =>
            this.element.insertAdjacentElement('afterend', node)
          )
          this.createdEl = domToInsert[domToInsert.length - 1]
        } else {
          this.element.insertAdjacentElement('afterend', domToInsert)
          this.createdEl = domToInsert
        }
        break
      case 'beforeEnd':
      default:
        if (Array.isArray(domToInsert)) {
          domToInsert.forEach((node) => this.element.appendChild(node))
          this.createdEl = domToInsert[domToInsert.length - 1]
        } else {
          this.element.insertAdjacentElement('beforeend', domToInsert)
          this.createdEl = domToInsert
        }
        break
    }

    // Привязываем instance
    if (this.createdEl && this.createdEl instanceof Element) {
      this.createdEl.instance = this
    } else if (Array.isArray(this.createdEl)) {
      this.createdEl.forEach((el) => {
        if (el instanceof Element) el.instance = this
      })
    }

    // Выполняем inline-скрипты
    this._executeScripts(content)

    // Modal
    if (this.options.type === 'modal' && this.createdEl) {
      let modalEl = null
      const bsBackdrop = this.options.backdrop
        ? this.options.backdropStatic
          ? 'static'
          : true
        : false
      const bsKeyboard = bsBackdrop !== 'static'

      if (
        this.createdEl.classList &&
        this.createdEl.classList.contains('modal')
      ) {
        modalEl = this.createdEl
      } else {
        modalEl = this.createdEl.querySelector('.modal')
      }
      if (modalEl) {
        modalEl.classList.add(this.options.placeHTMLclass)
        const modalInstance = bootstrap.Modal.getOrCreateInstance(modalEl, {
          backdrop: bsBackdrop,
          keyboard: bsKeyboard
        })
        modalInstance.show()

        modalEl.addEventListener('hidden.bs.modal', () => {
          this._debug('info', 'Modal скрыт')
          document.querySelector('body').focus()
          if (typeof this.onClassHide === 'function')
            this.onClassHide(this, modalEl)
          if (this.options.deleteAfterClose) {
            modalEl.remove()
            if (typeof this.onClassDelete === 'function')
              this.onClassDelete(this, modalEl)
          }
          this._closingHandler()
        })
      }
    }

    // Toast
    if (this.options.type === 'toast' && this.createdEl) {
      let toastEl = null
      if (
        this.createdEl.classList &&
        this.createdEl.classList.contains('toast')
      ) {
        toastEl = this.createdEl
      } else {
        toastEl = this.createdEl.querySelector('.toast')
      }
      if (toastEl) {
        this._createBackdrop()
        const toastInstance = bootstrap.Toast.getOrCreateInstance(toastEl, {
          autohide: false,
          animation: true
        })
        requestAnimationFrame(() => {
          requestAnimationFrame(() => toastInstance.show())
        })

        if (this.options.autoClose) {
          setTimeout(() => toastInstance.hide(), this.options.autoClose)
        }

        toastEl.addEventListener('hidden.bs.toast', () => {
          this._debug('info', 'Toast скрыт')
          this._closingHandler()
          if (typeof this.onClassHide === 'function')
            this.onClassHide(this, toastEl)
          if (this.options.deleteAfterClose) {
            toastEl.remove()
            if (typeof this.onClassDelete === 'function')
              this.onClassDelete(this, toastEl)
          }
        })
      }
    }

    // Alert
    if (this.options.type === 'alert' && this.createdEl) {
      let alertEl = null
      if (
        this.createdEl.classList &&
        this.createdEl.classList.contains('alert')
      ) {
        alertEl = this.createdEl
      } else {
        alertEl = this.createdEl.querySelector('.alert')
      }
      if (alertEl) {
        this._createBackdrop()
        // появление
        alertEl.classList.add('fade')
        alertEl.classList.remove('show')
        requestAnimationFrame(() => {
          requestAnimationFrame(() => alertEl.classList.add('show'))
        })

        if (this.options.autoClose) {
          setTimeout(() => this._closeAlert(alertEl), this.options.autoClose)
        }

        alertEl.addEventListener('close.bs.alert', () => {
          this._closingHandler()
          if (typeof this.onClassHide === 'function')
            this.onClassHide(this, alertEl)
        })
        alertEl.addEventListener('closed.bs.alert', () => {
          this._debug('info', 'Alert скрыт (closed.bs.alert)')
          if (this.options.deleteAfterClose) {
            alertEl.remove()
            if (typeof this.onClassDelete === 'function')
              this.onClassDelete(this, alertEl)
          }
        })
      }
    }

    if (typeof this.afterClassPlace === 'function') {
      this.afterClassPlace(this)
    }
  }

  // Выполняем inline-скрипты только из вставленного HTML
  _executeScripts(html) {
    const tempContainer = document.createElement('div')
    tempContainer.innerHTML = html.trim()
    const scripts = tempContainer.querySelectorAll('script')

    scripts.forEach((script) => {
      const newScript = document.createElement('script')
      Array.from(script.attributes).forEach((attr) =>
        newScript.setAttribute(attr.name, attr.value)
      )
      newScript.textContent = script.textContent
      document.body.appendChild(newScript)
      newScript.remove()
    })

    scripts.forEach((script) => script.remove())
  }

  _resolvePlaceIn(placeIn) {
    // stack для toast/alert
    if (
      placeIn === 'stack' &&
      (this.options.type === 'toast' || this.options.type === 'alert')
    ) {
      return this._getOrCreateStackContainer()
    }

    if (typeof placeIn === 'string') {
      if (placeIn === 'body') return document.body
      const foundEl = document.querySelector(placeIn)
      if (!foundEl) {
        this._debug(
          'warn',
          `Селектор "${placeIn}" не найден, используем <body> вместо него.`
        )
        return document.body
      }
      return foundEl
    } else if (placeIn instanceof Element) {
      return placeIn
    }
    return document.body
  }

  _getOrCreateStackContainer() {
    const [vertical, horizontal] = this._parseStackPosition(
      this.options.stackPosition
    )
    const stackId =
      this.options.stackId ||
      `stack-${this.options.type}-${vertical}-${horizontal}`
    let stackContainer = document.getElementById(stackId)
    if (stackContainer) {
      // сохранить stackId в опциях, чтобы backdrop шарился по ключу стека
      this.options.stackId = stackId
      return stackContainer
    }

    this.options.stackId = stackId
    stackContainer = document.createElement('div')
    stackContainer.id = stackId
    stackContainer.classList.add(
      `stack_pos-v-${vertical}`,
      `stack_pos-h-${horizontal}`
    )
    stackContainer.classList.add('notices_stack', `stack-${this.options.type}s`)

    document.body.appendChild(stackContainer)
    return stackContainer
  }

  _renderIcon() {
    const { icon } = this.options
    if (!icon || typeof icon !== 'string') return ''
    return `<div class="icon__wrap">${icon}</div>`
  }

  _parseStackPosition(posString) {
    const vSet = new Set(['top', 'bottom'])
    const hSet = new Set(['left', 'right'])
    const parts = posString.trim().toLowerCase().split(/\s+/)
    let vertical = 'center'
    let horizontal = 'center'
    parts.forEach((p) => {
      if (vSet.has(p)) vertical = p
      else if (hSet.has(p)) horizontal = p
    })
    return [vertical, horizontal]
  }

  _createToastHTML() {
    const { title, body, hasClose, buttons } = this.options
    const iconHtml = this._renderIcon()

    let btnsHtml = ''
    if (Array.isArray(buttons) && buttons.length) {
      btnsHtml = `<div class="btns__wrap">`
      buttons.forEach((btnObj) => {
        const btnTag = btnObj.tag || 'button'
        let otherAttrs = ''
        Object.keys(btnObj).forEach((key) => {
          if (!['tag', 'title', 'class', 'onClick'].includes(key)) {
            otherAttrs += ` ${key}="${btnObj[key]}"`
          }
        })
        btnsHtml += `\n          <${btnTag} class="${
          btnObj.class || ''
        }"${otherAttrs}>\n            ${
          btnObj.title || ''
        }\n          </${btnTag}>\n        `
      })
      btnsHtml += `</div>`
    }

    const closeBtnHtml = hasClose
      ? `<button type="button" class="btn-close" data-bs-dismiss="toast" aria-label="Close"></button>`
      : ''

    // Шапка создаётся, если есть либо заголовок, либо иконка
    const htmlHead =
      title || iconHtml
        ? `<div class="toast-header">
					${iconHtml}${title ? `<div class="toast-title">${title}</div>` : ''}
				</div>`
        : ''

    const htmlBody =
      body || btnsHtml
        ? `<div class="toast-body">${body || ''}${btnsHtml}</div>`
        : ''

    return `<div class="toast ${this.options.placeHTMLclass}" role="alert" aria-live="assertive" aria-atomic="true">
				${closeBtnHtml}${htmlHead}${htmlBody}
			</div>`
  }

  _createAlertHTML() {
    const { title, body, hasClose, buttons } = this.options
    const iconHtml = this._renderIcon()

    let btnsHtml = ''
    if (Array.isArray(buttons) && buttons.length) {
      btnsHtml = '<div class="btns__wrap">'
      buttons.forEach((btnObj) => {
        const btnTag = btnObj.tag || 'button'
        let otherAttrs = ''
        Object.keys(btnObj).forEach((key) => {
          if (!['tag', 'title', 'class', 'onClick'].includes(key)) {
            otherAttrs += ` ${key}="${btnObj[key]}"`
          }
        })
        btnsHtml += `\n          <${btnTag} class="${
          btnObj.class || ''
        }"${otherAttrs}>\n            ${
          btnObj.title || ''
        }\n          </${btnTag}>\n        `
      })
      btnsHtml += '</div>'
    }

    const alertClass =
      this.options.class !== '' ? this.options.class : 'alert-warning'
    const closeBtnHtml = hasClose
      ? `<button type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>`
      : ''
    const alertTitle = title ? `<h4 class="alert-heading">${title}</h4>` : ''

    return `
			<div class="alert ${
        this.options.placeHTMLclass
      } ${alertClass} alert-dismissible fade" role="alert">
				${closeBtnHtml}
				${iconHtml}
				${alertTitle}
				<div>${body || ''}</div>
				${btnsHtml}
			</div>
		`
  }

  _closeAlert(alertEl) {
    const alertInstance = bootstrap.Alert.getOrCreateInstance(alertEl)
    alertInstance.close()
    this._closingHandler()
  }

  _closeIfPossible() {
    if (this._closing) return
    this._closing = true

    if (!this.createdEl) return

    if (this.options.type === 'toast') {
      const toastEl =
        this.createdEl.classList && this.createdEl.classList.contains('toast')
          ? this.createdEl
          : this.createdEl.querySelector('.toast')
      if (toastEl) bootstrap.Toast.getOrCreateInstance(toastEl).hide()
    } else if (this.options.type === 'alert') {
      const alertEl =
        this.createdEl.classList && this.createdEl.classList.contains('alert')
          ? this.createdEl
          : this.createdEl.querySelector('.alert')
      if (alertEl) bootstrap.Alert.getOrCreateInstance(alertEl).close()
    } else if (this.options.type === 'modal') {
      let modalEl = this.createdEl.querySelector('.modal')
      if (!modalEl && this.createdEl.classList?.contains('modal'))
        modalEl = this.createdEl
      if (modalEl) bootstrap.Modal.getOrCreateInstance(modalEl).hide()
    } else if (this.options.type === 'html') {
      this.createdEl.remove()
      this._closingHandler()
    }
  }

  _closingHandler() {
    this._removeBackdrop()
  }

  static ask(userOptions = {}) {
    return new Promise((resolve, reject) => {
      const inst = new placeHTML({
        ...userOptions,
        afterPlace(instance) {
          if (typeof userOptions.afterPlace === 'function') {
            userOptions.afterPlace(instance)
          }

          if (Array.isArray(instance.options.buttons)) {
            instance.options.buttons.forEach((btnObj) => {
              const btnTag = btnObj.tag || 'button'
              const possibleButtons =
                instance.createdEl.querySelectorAll(btnTag)
              possibleButtons.forEach((el) => {
                if (el.textContent.trim() === (btnObj.title || '').trim()) {
                  el.addEventListener('click', (event) => {
                    if (instance._closing) return

                    if (typeof btnObj.onClick === 'function') {
                      btnObj.onClick(instance, event, resolve, reject)
                    } else {
                      if (
                        typeof userOptions.onConfirm === 'function' &&
                        btnObj.title.toLowerCase().includes('ok')
                      ) {
                        userOptions.onConfirm(instance, event, resolve, reject)
                      } else if (
                        typeof userOptions.onCancel === 'function' &&
                        btnObj.title.toLowerCase().includes('cancel')
                      ) {
                        userOptions.onCancel(instance, event, resolve, reject)
                      } else {
                        resolve(null)
                        instance._closeIfPossible()
                      }
                    }
                  })
                }
              })
            })
          }
        },
        onHide(instance, domEl) {
          if (typeof userOptions.onHide === 'function') {
            userOptions.onHide(instance, domEl)
          }
          if (!instance._closing) {
            if (typeof userOptions.onCancel === 'function') {
              userOptions.onCancel(instance, null, resolve, reject)
            } else {
              reject('cancel')
            }
          }
        }
      })
      return inst
    })
  }

  /**
   * BACKDROP (синглтон на стек + рефкаунт)
   */
  _createBackdrop() {
    if (this.options.backdrop !== true) return

    // ключ для шаринга backdrop: конкретный стек, иначе глобально по типу
    const key = this.options.stackId || `${this.options.type}-global`
    let data = placeHTML._backdrops.get(key)

    // если уже создан и в DOM — увеличиваем счётчик
    if (data && data.el && document.body.contains(data.el)) {
      data.count++
      this.backdropEl = data.el
      this._backdropKey = key
      return
    }

    // создаём новый
    const el = document.createElement('div')
    el.classList.add(
      this.options.backdropClassMain,
      'fade',
      this.options.backdropClass
    )
    document.body.appendChild(el)

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        el.classList.add('show')
      })
    })

    let escHandler = null
    if (this.options.backdropStatic === false) {
      escHandler = (e) => {
        if (e.key === 'Escape') this._closeIfPossible()
      }
      document.addEventListener('keydown', escHandler)
      el.addEventListener('click', () => this._closeIfPossible())
    }

    this.backdropEl = el
    this._backdropKey = key
    placeHTML._backdrops.set(key, { el, count: 1, escHandler })
  }

  _removeBackdrop() {
    if (!this._backdropKey) return
    const data = placeHTML._backdrops.get(this._backdropKey)
    if (!data) return

    data.count--
    if (data.count > 0) {
      // ещё используется другими инстансами
      return
    }

    const { el, escHandler } = data
    el.classList.remove('show')

    setTimeout(() => {
      if (el && el.parentNode) el.parentNode.removeChild(el)
      if (escHandler) document.removeEventListener('keydown', escHandler)
      placeHTML._backdrops.delete(this._backdropKey)
      delete this._backdropKey
      delete this.backdropEl
    }, 500)
  }

  _debug(level, ...args) {
    if (!this.options.debug) return
    switch (level) {
      case 'info':
        console.info('[placeHTML]', ...args)
        break
      case 'warn':
        console.warn('[placeHTML]', ...args)
        break
      case 'error':
        console.error('[placeHTML]', ...args)
        break
      default:
        console.log('[placeHTML]', ...args)
    }
  }
}

/**
 * Авто-инициализация по data-атрибуту
 */
const dataAttr = placeHTML.defaultOptions.dataAttrName

document.querySelectorAll(`[${dataAttr}]`).forEach(el => {
  const configStr = el.getAttribute(dataAttr)
  if (configStr) {
    try {
      const config = new Function('return ' + configStr)()
      config.placeIn = el
      config.type = 'html_place'
      config.caller = el
      new placeHTML(config)
    } catch (e) {
      console.error('placeHTML: ошибка парсинга data-атрибута:', e)
    }
  }
})
